"use strict";
/**
 * Professional Spell Check Manager
 * Manages spell checking for segments with lazy loading, caching, and batch processing
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.spellCheckManager = void 0;
const spellCheckService_1 = require("./spellCheckService");
class SpellCheckManager {
    constructor() {
        this.cache = {};
        this.CACHE_DURATION = 5 * 60 * 1000; // 5 minut
        this.batchProgress = null;
        this.progressCallbacks = [];
        // Notify UI when cache updates (for counts / filters recalculation)
        this.cacheListeners = [];
    }
    notifyCacheListeners() {
        this.cacheListeners.forEach(cb => {
            try {
                cb();
            }
            catch { }
        });
    }
    addCacheListener(cb) {
        this.cacheListeners.push(cb);
        return () => {
            this.cacheListeners = this.cacheListeners.filter(x => x !== cb);
        };
    }
    // Allow components to manually upsert cache entries (e.g., SpellCheckDisplay)
    updateCache(segmentId, text, result) {
        this.cache[segmentId] = {
            result,
            timestamp: Date.now(),
            textHash: this.hashText(text)
        };
        this.notifyCacheListeners();
    }
    /**
     * Get spell check status for a single segment (with caching)
     */
    async getSegmentSpellCheck(segment) {
        const textHash = this.hashText(segment.corrected_text);
        const cached = this.cache[segment.segment_id];
        // Check if we have valid cached result
        if (cached &&
            cached.textHash === textHash &&
            Date.now() - cached.timestamp < this.CACHE_DURATION) {
            return {
                hasErrors: cached.result.errors.length > 0,
                errorCount: cached.result.errors.length,
                checkedAt: new Date(cached.timestamp).toISOString(),
                isChecking: false
            };
        }
        try {
            // Skip empty or very short texts
            if (!segment.corrected_text.trim() || segment.corrected_text.trim().length < 3) {
                return {
                    hasErrors: false,
                    errorCount: 0,
                    checkedAt: new Date().toISOString(),
                    isChecking: false
                };
            }
            const result = await spellCheckService_1.spellCheckService.checkSpelling(segment.corrected_text);
            // Cache the result and notify listeners (so SegmentFilters can update counts)
            this.cache[segment.segment_id] = {
                result,
                timestamp: Date.now(),
                textHash
            };
            this.notifyCacheListeners();
            return {
                hasErrors: result.errors.length > 0,
                errorCount: result.errors.length,
                checkedAt: new Date().toISOString(),
                isChecking: false
            };
        }
        catch (error) {
            console.error('Spell check failed for segment:', segment.segment_id, error);
            return {
                hasErrors: false,
                errorCount: 0,
                checkedAt: new Date().toISOString(),
                isChecking: false
            };
        }
    }
    /**
     * Batch spell check for entire project with progress tracking
     */
    async batchSpellCheckProject(segments, onProgress) {
        if (this.batchProgress?.isRunning) {
            throw new Error('Batch spell check is already running');
        }
        this.batchProgress = {
            total: segments.length,
            processed: 0,
            current: '',
            isRunning: true
        };
        if (onProgress) {
            this.progressCallbacks.push(onProgress);
        }
        try {
            let totalErrors = 0;
            let segmentsWithErrors = 0;
            const updatedSegments = [];
            // Process segments in batches to avoid overwhelming the system
            const BATCH_SIZE = 5;
            for (let i = 0; i < segments.length; i += BATCH_SIZE) {
                const batch = segments.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(batch.map(async (segment, batchIndex) => {
                    this.batchProgress.current = `Segment ${segment.sequence_number}`;
                    this.notifyProgress();
                    const status = await this.getSegmentSpellCheck(segment);
                    this.batchProgress.processed++;
                    this.notifyProgress();
                    if (status.hasErrors) {
                        segmentsWithErrors++;
                        totalErrors += status.errorCount;
                    }
                    // Update segment with spell check status
                    const updatedSegment = {
                        ...segment,
                        spellCheck: status
                    };
                    return { updatedSegment, originalIndex: i + batchIndex };
                }));
                // Add results to updatedSegments in correct order
                batchResults.forEach(({ updatedSegment }) => {
                    updatedSegments.push(updatedSegment);
                });
                // Small delay between batches to prevent UI freezing
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            const summary = {
                totalSegments: segments.length,
                segmentsWithErrors,
                totalErrors,
                lastChecked: new Date().toISOString(),
                isChecking: false
            };
            return { summary, updatedSegments };
        }
        finally {
            this.batchProgress = null;
            this.progressCallbacks = [];
        }
    }
    /**
     * Get cached spell check result for segment
     */
    getCachedSpellCheck(segmentId, text) {
        const cached = this.cache[segmentId];
        if (!cached)
            return null;
        const textHash = this.hashText(text);
        if (cached.textHash !== textHash ||
            Date.now() - cached.timestamp > this.CACHE_DURATION) {
            return null;
        }
        return cached.result;
    }
    /**
     * Clear cache for specific segment or entire cache
     */
    clearCache(segmentId) {
        if (segmentId) {
            delete this.cache[segmentId];
        }
        else {
            this.cache = {};
        }
    }
    /**
     * Get current batch progress
     */
    getBatchProgress() {
        return this.batchProgress;
    }
    /**
     * Cancel running batch spell check
     */
    cancelBatchSpellCheck() {
        if (this.batchProgress) {
            this.batchProgress.isRunning = false;
            this.batchProgress = null;
            this.progressCallbacks = [];
        }
    }
    hashText(text) {
        // Simple hash function for text comparison
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
            const char = text.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return hash.toString();
    }
    notifyProgress() {
        if (this.batchProgress) {
            this.progressCallbacks.forEach(callback => callback(this.batchProgress));
        }
    }
}
// Export singleton instance
exports.spellCheckManager = new SpellCheckManager();
